import { Component, type ReactNode } from "react"
import { AlertTriangle, RefreshCw01 } from "@untitledui/icons"

// Quick Estimate 3 — per-step error boundary.
//
// Wraps each step content so a render-time throw doesn't whitescreen
// the entire wizard. Photos already saved in sessionStorage survive
// the reload — clicking "Reload step" remounts the step's subtree
// and rehydration kicks in.
//
// Note: this catches render errors. Async errors (a fetch rejecting
// in a useEffect) are NOT caught — those have their own per-call
// try/catch in the photo step + submit pipeline.

interface Props {
  /** Surfaced in the recovery card. "Photos" / "Vehicle" / "Review". */
  stepLabel: string
  children: ReactNode
}

interface State {
  errored: boolean
  message: string | null
}

export class StepErrorBoundary extends Component<Props, State> {
  state: State = { errored: false, message: null }

  static getDerivedStateFromError(err: unknown): State {
    return {
      errored: true,
      message: err instanceof Error ? err.message : null,
    }
  }

  componentDidCatch(err: unknown) {
    // Lightweight surface — we don't have a telemetry plumbing yet.
    // When we do, this is the call site.
    console.error(`[QE3:${this.props.stepLabel}]`, err)
  }

  reset = () => {
    this.setState({ errored: false, message: null })
  }

  render() {
    if (!this.state.errored) return this.props.children

    return (
      <div className="text-center py-8 space-y-5">
        <div className="mx-auto size-14 rounded-full bg-amber-50 grid place-items-center">
          <AlertTriangle className="size-6 text-amber-600" aria-hidden />
        </div>
        <div className="space-y-1.5 max-w-sm mx-auto">
          <h3 className="text-[18px] font-bold text-zinc-900 leading-tight">
            Something glitched on this step.
          </h3>
          <p className="text-[14px] text-zinc-500 leading-relaxed">
            Your work is saved — reload the step to continue.
          </p>
          {this.state.message && (
            <p className="mt-2 text-[11.5px] font-mono text-zinc-400 break-words">
              {this.state.message}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={this.reset}
          className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-zinc-900 hover:bg-black text-white text-[13.5px] font-semibold shadow-md transition-colors duration-150"
        >
          <RefreshCw01 className="size-4" aria-hidden />
          Reload step
        </button>
      </div>
    )
  }
}
